"use client"

import { Box, Heading, SimpleGrid, Stack, Text, VStack } from "@chakra-ui/react"
import { type FC } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import MarkdownParser from "@/components/MarkdownParser"
import { type OutcomeBlock as OutcomeBlockType } from "@/types/database"

import {
  type ContentBlockTitleNumberProps,
  formatContentBlockTitleNumber,
} from "./ContentBlockTitle"

interface OutcomeBlockProps extends ContentBlockTitleNumberProps {
  block: OutcomeBlockType
}

interface OutcomeItem {
  description: string
  value: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const parseJsonValue = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value
  }

  try {
    return JSON.parse(value) as unknown
  } catch {
    return value
  }
}

const getString = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    const trimmedValue = value.trim()

    return trimmedValue.length > 0 ? trimmedValue : undefined
  }

  if (typeof value === "number") {
    return String(value)
  }

  return undefined
}

const unwrapOutcomeRecord = (
  item: Record<string, unknown>
): Record<string, unknown> => {
  const nestedKeys = [
    "item",
    "outcome",
    "outcome_id",
    "outcome_item",
    "outcome_item_id",
    "outcome_items_id",
  ]

  for (const key of nestedKeys) {
    const nestedValue = item[key]

    if (isRecord(nestedValue)) {
      return nestedValue
    }
  }

  return item
}

const normalizeOutcomeItem = (item: unknown): OutcomeItem | null => {
  const parsedItem = parseJsonValue(item)

  if (!isRecord(parsedItem)) {
    return null
  }

  const outcomeRecord = unwrapOutcomeRecord(parsedItem)
  const value =
    getString(outcomeRecord.value) ??
    getString(outcomeRecord.metric) ??
    getString(outcomeRecord.stat) ??
    getString(outcomeRecord.title) ??
    getString(outcomeRecord.label)
  const description =
    getString(outcomeRecord.description) ??
    getString(outcomeRecord.subtitle) ??
    getString(outcomeRecord.text) ??
    getString(outcomeRecord.copy)

  if (!value || !description) {
    return null
  }

  return {
    description,
    value,
  }
}

const normalizeOutcomeItems = (value: unknown): OutcomeItem[] => {
  const parsedValue = parseJsonValue(value)

  if (Array.isArray(parsedValue)) {
    return parsedValue.reduce<OutcomeItem[]>((items, valueItem) => {
      const normalizedItem = normalizeOutcomeItem(valueItem)

      if (normalizedItem !== null) {
        items.push(normalizedItem)
      }

      return items
    }, [])
  }

  if (isRecord(parsedValue)) {
    const nestedItems =
      parsedValue.items ??
      parsedValue.outcomes ??
      parsedValue.metrics ??
      parsedValue.stats ??
      parsedValue.values

    if (nestedItems) {
      return normalizeOutcomeItems(nestedItems)
    }

    return Object.entries(parsedValue).reduce<OutcomeItem[]>(
      (items, [valueKey, descriptionValue]) => {
        const normalizedItem = normalizeOutcomeItem({
          description: descriptionValue,
          value: valueKey,
        })

        if (normalizedItem !== null) {
          items.push(normalizedItem)
        }

        return items
      },
      []
    )
  }

  return []
}

const getFirstOutcomeItems = (values: unknown[]): OutcomeItem[] => {
  for (const value of values) {
    const outcomeItems = normalizeOutcomeItems(value)

    if (outcomeItems.length > 0) {
      return outcomeItems
    }
  }

  return []
}

const OutcomeBlock: FC<OutcomeBlockProps> = ({ block, titleNumber }) => {
  const { darker_bg, description, headline, title, values } = block
  const outcomeItems = getFirstOutcomeItems([values])
  const formattedTitleNumber = formatContentBlockTitleNumber(titleNumber)

  if (outcomeItems.length === 0) {
    return null
  }
  const trimmedTitle = title?.trim()
  const hasTitle = Boolean(trimmedTitle)
  return (
    <Stack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="outcome-block internal-padding"
      gap={{ base: 4, lg: 5 }}
    >
      {(hasTitle || description || headline) && (
        <VStack alignItems="flex-start" gap={3} pb={8} w="full">
          <BlurFade flexDirection="column" gap={3} inView width="100%">
            {formattedTitleNumber && (
              <Text
                as="span"
                color={{ base: "primary.500", _dark: "primary.200" }}
                fontFamily="mono"
                fontSize="0.8rem"
                fontWeight="medium"
                lineHeight="inherit"
              >
                {formattedTitleNumber}
              </Text>
            )}
            {title && (
              <Heading
                as="h2"
                color="fg.muted"
                fontFamily="heading"
                fontSize="xl"
              >
                {title}
              </Heading>
            )}
            {headline && (
              <Heading
                as="h3"
                fontFamily="mono"
                fontSize="sm"
                lineHeight="1"
                size="2xl"
              >
                {headline}
              </Heading>
            )}
            {description && (
              <MarkdownParser
                color="fg.muted"
                fontSize="md"
                markdown={description}
              />
            )}
          </BlurFade>
        </VStack>
      )}
      <BlurFade flexDirection="column" gap={3} inView width="100%">
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} w="full">
          {outcomeItems.map((item) => (
            <Box
              bg="bg.panel"
              borderRadius="lg"
              key={`${item.value}-${item.description}`}
              minH={{ base: "108px", md: "128px" }}
              p={{ base: 5, md: 6 }}
            >
              <Text
                fontSize={{ base: "2xl", md: "3xl" }}
                fontWeight="bold"
                lineHeight="1"
                mb={2}
              >
                {item.value}
              </Text>
              <Text
                color="fg.muted"
                fontSize={{ base: "sm", md: "md" }}
                fontWeight="semibold"
                lineHeight="1.25"
              >
                {item.description}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </BlurFade>
    </Stack>
  )
}

export default OutcomeBlock
